// Şube + coğrafi (il/ilçe) API client (MOD-11 / K-2).
//
// Admin CRUD `/api/v1/admin/branches` — list/get branch.view, create/update/delete
// branch.manage. Public coğrafi liste `/api/v1/cities` (81 il, plaka sıralı) +
// `/api/v1/cities/{slug}/districts` (harita/form filtresi). POST → GuidActionResponse
// (yeni id string), PUT → Boolean (rowVersion ile iyimser kilit). İş kuralları BE'de:
// koordinatsız → VALIDATION_ERROR/400, BRANCH_CITY_NOT_FOUND, BRANCH_DISTRICT_CITY_MISMATCH.

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import type {
  Branch,
  BranchDtoWire,
  BranchInput,
  BranchListQuery,
  City,
  CityDtoWire,
  CreateBranchRequest,
  District,
  DistrictDtoWire,
  UpdateBranchRequest,
} from '@features/branches/types';

const BASE = '/api/v1/admin/branches';

function mapBranch(d: BranchDtoWire): Branch {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    slug: d.slug ?? '',
    addressLine: d.addressLine ?? '',
    cityId: d.cityId ?? '',
    cityName: d.cityName ?? '',
    citySlug: d.citySlug ?? '',
    districtId: d.districtId ?? null,
    districtName: d.districtName ?? null,
    latitude: d.latitude ?? 0,
    longitude: d.longitude ?? 0,
    phone: d.phone ?? null,
    email: d.email ?? null,
    workingHours: d.workingHours ?? null,
    order: d.order ?? 0,
    isActive: d.isActive ?? false,
    rowVersion: d.rowVersion ?? null,
    createdAt: d.createdAt ?? '',
    updatedAt: d.updatedAt ?? '',
  };
}

function mapCity(d: CityDtoWire): City {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    slug: d.slug ?? '',
    plateCode: d.plateCode ?? 0,
  };
}

function mapDistrict(d: DistrictDtoWire): District {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    slug: d.slug ?? '',
    cityId: d.cityId ?? '',
  };
}

function toBody(input: BranchInput): CreateBranchRequest {
  return {
    name: input.name,
    slug: input.slug ?? null,
    addressLine: input.addressLine,
    cityId: input.cityId,
    districtId: input.districtId ?? null,
    latitude: input.latitude,
    longitude: input.longitude,
    phone: input.phone ?? null,
    email: input.email ?? null,
    workingHours: input.workingHours ?? null,
    order: input.order,
    isActive: input.isActive,
  };
}

export const branchesApi = {
  async list(
    query: BranchListQuery = {},
  ): Promise<{ items: Branch[]; total: number; page: number; pageSize: number }> {
    const params: Record<string, string | number | boolean> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.cityId) params.cityId = query.cityId;
    if (query.isActive !== undefined) params.isActive = query.isActive;
    if (query.search) params.search = query.search;
    const res = await http.get<ActionResponse<BranchDtoWire>>(BASE, { params });
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapBranch),
      total: pagination?.totalCount ?? items.length,
      page: pagination?.page ?? query.page ?? 1,
      pageSize: pagination?.pageSize ?? query.pageSize ?? 20,
    };
  },

  async get(id: string): Promise<Branch> {
    return mapBranch(unwrap(await http.get<ActionResponse<BranchDtoWire>>(`${BASE}/${id}`)));
  },

  /** POST → yeni şubenin GUID'i (GuidActionResponse). */
  async create(input: BranchInput): Promise<string> {
    return unwrap(await http.post<ActionResponse<string>>(BASE, toBody(input)));
  },

  /** PUT → Boolean; rowVersion iyimser kilit için gönderilir (409 → concurrency). */
  async update(id: string, input: BranchInput, rowVersion: string | null): Promise<void> {
    const body: UpdateBranchRequest = { ...toBody(input), rowVersion: rowVersion ?? null };
    unwrap(await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body));
  },

  async remove(id: string): Promise<void> {
    unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`));
  },
};

export const citiesApi = {
  async list(): Promise<City[]> {
    const res = await http.get<ActionResponse<CityDtoWire>>('/api/v1/cities');
    return unwrapList(res).items.map(mapCity);
  },

  async districts(citySlug: string): Promise<District[]> {
    const res = await http.get<ActionResponse<DistrictDtoWire>>(
      `/api/v1/cities/${citySlug}/districts`,
    );
    return unwrapList(res).items.map(mapDistrict);
  },
};
